import {
  ALL_PORTS,
  ANYWHERE_V4,
  ANYWHERE_V6,
  FirewallProtocol,
  PORT_PROTOCOLS,
  isAllPorts,
  isIpCidr,
  isPortSpec,
  type FirewallDirection,
  type FirewallRule,
  type FirewallRuleInput,
} from "@cloudserver/contracts";
import clsx from "clsx";
import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AskAssistantButton, firewallRuleQuestion, type AskRequest } from "../assistant/ask.js";
import { Alert, Button, Select, TextField } from "../ui/primitives.js";

/** One rule as the editor holds it: addresses as the text a person types. */
export interface RuleDraft {
  key: string;
  direction: FirewallDirection;
  protocol: FirewallProtocol;
  port: string;
  ips: string;
  description: string;
}

let counter = 0;
const nextKey = () => `rule-${++counter}`;

export const splitIps = (text: string): string[] =>
  text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

export function newDraft(
  direction: FirewallDirection,
  init: Partial<Omit<RuleDraft, "key" | "direction">> = {},
): RuleDraft {
  return {
    key: nextKey(),
    direction,
    protocol: "tcp",
    port: "",
    ips: direction === "in" ? `${ANYWHERE_V4}, ${ANYWHERE_V6}` : "",
    description: "",
    ...init,
  };
}

/** The rules a new firewall starts with: SSH and ping from anywhere. */
export const defaultInbound = (): RuleDraft[] => [
  newDraft("in", { protocol: "tcp", port: "22", description: "SSH" }),
  newDraft("in", { protocol: "icmp", description: "ICMP" }),
];

export const draftsOf = (rules: FirewallRule[]): RuleDraft[] =>
  rules.map((r) =>
    newDraft(r.direction, {
      protocol: r.protocol,
      port: r.port ?? "",
      ips: (r.direction === "in" ? r.sourceIps : r.destinationIps).join(", "),
      description: r.description ?? "",
    }),
  );

export interface DraftErrors {
  port?: string;
  ips?: string;
}

/** Translation keys of what is wrong with one draft. */
export function draftErrors(draft: RuleDraft): DraftErrors {
  const errors: DraftErrors = {};
  if (PORT_PROTOCOLS.includes(draft.protocol) && !isPortSpec(draft.port)) {
    errors.port = "firewalls.portInvalid";
  }
  const ips = splitIps(draft.ips);
  if (ips.length === 0) errors.ips = "firewalls.ipsRequired";
  else if (!ips.every(isIpCidr)) errors.ips = "firewalls.ipsInvalid";
  return errors;
}

export const hasErrors = (drafts: RuleDraft[]): boolean =>
  drafts.some((d) => Object.keys(draftErrors(d)).length > 0);

/** The API form of the drafts (assumes they passed `draftErrors`). */
export const rulesOf = (drafts: RuleDraft[]): FirewallRuleInput[] =>
  drafts.map((d) => {
    const ips = splitIps(d.ips);
    return {
      direction: d.direction,
      protocol: d.protocol,
      port: PORT_PROTOCOLS.includes(d.protocol) ? d.port.trim() : null,
      sourceIps: d.direction === "in" ? ips : [],
      destinationIps: d.direction === "out" ? ips : [],
      description: d.description.trim() || null,
    };
  });

/** Comparable form of drafts, to tell whether the editor holds unsaved changes. */
export const draftsSignature = (drafts: RuleDraft[]): string =>
  JSON.stringify(
    drafts.map(({ direction, protocol, port, ips, description }) => ({
      direction,
      protocol,
      port: PORT_PROTOCOLS.includes(protocol) ? port.trim() : "",
      ips: splitIps(ips),
      description: description.trim(),
    })),
  );

/** A toggle shortcut under a field ("Any IPv4", "Any port"). */
function Chip({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={clsx(
        "min-h-7 rounded-full border px-3 text-xs",
        pressed
          ? "border-accent bg-accent-soft text-accent-strong"
          : "border-line text-muted hover:text-text",
      )}
    >
      {children}
    </button>
  );
}

function RuleRow({
  draft,
  onChange,
  onRemove,
  readOnly,
  showErrors,
  ask,
}: {
  draft: RuleDraft;
  onChange: (draft: RuleDraft) => void;
  onRemove: () => void;
  readOnly: boolean;
  showErrors: boolean;
  /** «Спросить ассистента» about this rule (a saved firewall's rules only). */
  ask?: AskRequest;
}) {
  const { t } = useTranslation();
  const errors = showErrors ? draftErrors(draft) : {};
  const hasPorts = PORT_PROTOCOLS.includes(draft.protocol);
  const allPorts = hasPorts && isAllPorts(draft.port);
  const ips = splitIps(draft.ips);
  const toggle = (cidr: string) =>
    onChange({
      ...draft,
      ips: (ips.includes(cidr) ? ips.filter((ip) => ip !== cidr) : [...ips, cidr]).join(", "),
    });
  return (
    <li className="rounded-xl border border-line bg-surface p-4">
      <fieldset disabled={readOnly} className="flex flex-col gap-3">
        <legend className="sr-only">
          {t(`firewalls.direction.${draft.direction}`)} {draft.protocol} {draft.port}
        </legend>
        <div className="grid gap-3 sm:grid-cols-[9rem_10rem_1fr_auto] sm:items-start">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium" aria-hidden>
              {t("firewalls.protocol")}
            </span>
            <Select
              label={t("firewalls.protocol")}
              value={draft.protocol}
              onChange={(e) => onChange({ ...draft, protocol: e.target.value as FirewallProtocol })}
              className="min-h-10"
            >
              {FirewallProtocol.options.map((p) => (
                <option key={p} value={p}>
                  {p.toUpperCase()}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <TextField
              label={t("firewalls.port")}
              value={hasPorts ? draft.port : ""}
              placeholder={hasPorts ? t("firewalls.portHint") : t("firewalls.portNone")}
              disabled={!hasPorts || allPorts || readOnly}
              onChange={(e) => onChange({ ...draft, port: e.target.value })}
              inputMode="numeric"
              error={errors.port ? t(errors.port) : undefined}
            />
            {hasPorts && (
              <div className="flex flex-wrap gap-2">
                <Chip
                  pressed={allPorts}
                  onClick={() => onChange({ ...draft, port: allPorts ? "" : ALL_PORTS })}
                >
                  {t("firewalls.anyPort")}
                </Chip>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <TextField
              label={
                draft.direction === "in" ? t("firewalls.sources") : t("firewalls.destinations")
              }
              value={draft.ips}
              onChange={(e) => onChange({ ...draft, ips: e.target.value })}
              spellCheck={false}
              placeholder="203.0.113.0/24"
              error={errors.ips ? t(errors.ips) : undefined}
            />
            <div className="flex flex-wrap gap-2">
              {[
                [ANYWHERE_V4, t("firewalls.anyIpv4")],
                [ANYWHERE_V6, t("firewalls.anyIpv6")],
              ].map(([cidr = "", label = ""]) => (
                <Chip key={cidr} pressed={ips.includes(cidr)} onClick={() => toggle(cidr)}>
                  {label}
                </Chip>
              ))}
            </div>
          </div>
          <span className="flex items-start justify-self-end sm:mt-6">
            {ask && (
              <AskAssistantButton
                compact
                request={ask}
                label={t("assistant.ask.aboutRule")}
                className="min-h-9 p-2"
              />
            )}
            <button
              type="button"
              onClick={onRemove}
              aria-label={t("firewalls.removeRule")}
              title={t("firewalls.removeRule")}
              className="rounded-lg p-2 text-muted hover:bg-surface-2 hover:text-danger"
            >
              <X className="size-5" aria-hidden />
            </button>
          </span>
        </div>
        {allPorts && (
          <Alert tone={draft.direction === "in" ? "warn" : "neutral"}>
            {t(
              draft.direction === "in" ? "firewalls.anyPortInbound" : "firewalls.anyPortOutbound",
              {
                protocol: draft.protocol.toUpperCase(),
              },
            )}
          </Alert>
        )}
        <TextField
          label={t("firewalls.description")}
          value={draft.description}
          maxLength={255}
          onChange={(e) => onChange({ ...draft, description: e.target.value })}
          placeholder={t("firewalls.descriptionHint")}
        />
      </fieldset>
    </li>
  );
}

/** Editor of one direction's rules: a card per rule, add and remove. */
export function RuleEditor({
  direction,
  drafts,
  onChange,
  readOnly = false,
  showErrors,
  askAbout,
}: {
  direction: FirewallDirection;
  drafts: RuleDraft[];
  onChange: (drafts: RuleDraft[]) => void;
  readOnly?: boolean;
  showErrors: boolean;
  /** The firewall the rules belong to: each rule gets «Спросить ассистента». */
  askAbout?: { id: string; name: string };
}) {
  const { t } = useTranslation();
  const own = drafts.filter((d) => d.direction === direction);
  const replace = (key: string, next: RuleDraft | null) =>
    onChange(drafts.flatMap((d) => (d.key !== key ? [d] : next ? [next] : [])));
  return (
    <div className="flex flex-col gap-3">
      {own.length === 0 ? (
        <p className="rounded-xl border border-dashed border-line px-4 py-3 text-sm text-muted">
          {direction === "in" ? t("firewalls.noInbound") : t("firewalls.noOutbound")}
        </p>
      ) : (
        <ul className="flex flex-col gap-3" aria-label={t(`firewalls.direction.${direction}`)}>
          {own.map((d, index) => (
            <RuleRow
              key={d.key}
              draft={d}
              readOnly={readOnly}
              showErrors={showErrors}
              {...(askAbout ? { ask: firewallRuleQuestion(t, askAbout, d, index) } : {})}
              onChange={(next) => replace(d.key, next)}
              onRemove={() => replace(d.key, null)}
            />
          ))}
        </ul>
      )}
      <div>
        <Button
          variant="secondary"
          disabled={readOnly}
          icon={<Plus className="size-4" aria-hidden />}
          onClick={() => onChange([...drafts, newDraft(direction)])}
        >
          {t("firewalls.addRule")}
        </Button>
      </div>
    </div>
  );
}
